<script setup lang="ts">
import { useCustomRockGradingStore } from '@/stores/customRockGrading'
import { storeToRefs } from 'pinia'
import { DeleteDialog, DataTableWrapper } from '@haskoning/vue-component-library'
import { computed } from 'vue'
import type { CustomRockGrading } from '@/stores/type/customRockGrading.type'
import CustomRockGradingTableRow from '@/components/materials/custom-rock-grading/CustomRockGradingTableRow.vue'
import { useProjectStore } from '@/stores/project'

const projectStore = useProjectStore()
const customRockGradingStore = useCustomRockGradingStore()

const { currentItem: currentProjectItem } = storeToRefs(projectStore)
const {
  fetchLoading,
  deleteLoading,
  deleteDialog,
  items,
  selectedItems,
  canAddCustomRockGrading,
  canChangeCustomRockGrading,
  canDeleteCustomRockGrading
} = storeToRefs(customRockGradingStore)

const isCheckboxShown = computed(
  () =>
    canAddCustomRockGrading.value ||
    canChangeCustomRockGrading.value ||
    canDeleteCustomRockGrading.value
)

const headers = computed(() => [
  { title: '', align: isCheckboxShown.value ? 'left' : ' d-none' },
  { title: 'Number', align: 'center' },
  { title: 'Name', align: 'left', width: '250px' },
  { title: 'NLL [kg]', align: 'center' },
  { title: 'NUL [kg]', align: 'center' },
  { title: 'yNLL [%]', align: 'center' },
  { title: 'yNUL [%]', align: 'center' },
  { title: 'Colour', align: 'center' },
  { title: 'Created by', align: 'left' },
  { title: 'Edit', align: isCheckboxShown.value ? 'center' : ' d-none' }
])

const filterItemsBySearch = <T extends CustomRockGrading>(
  items: T[],
  search: string,
  keys: (keyof T)[]
): T[] => {
  if (!search) return items
  return items.filter((item) =>
    keys.some((key) => item[key]?.toString().toLowerCase().includes(search.toLowerCase()))
  )
}

const getFilteredItems = (items: CustomRockGrading[], search: string): CustomRockGrading[] => {
  return filterItemsBySearch(items, search, ['name'])
}

const addCustomRockGrading = async () => {
  const newCustomRockGrading = customRockGradingStore.createNewCustomRockGrading(
    currentProjectItem.value.id
  )
  await customRockGradingStore.create(newCustomRockGrading)
}
</script>

<template>
  <template v-if="fetchLoading">
    <div class="d-flex justify-center align-center fill-height">
      <v-progress-circular indeterminate color="primary" size="64"></v-progress-circular>
    </div>
  </template>
  <template v-else>
    <DataTableWrapper
      :title="'Custom Rock Gradings'"
      :headers="headers"
      :items="items"
      :selected-items="selectedItems"
      :get-filtered-items="getFilteredItems"
      :can-change-title="false"
      :can-add-item="canAddCustomRockGrading"
      :can-change-item="canChangeCustomRockGrading"
      :can-delete-item="canDeleteCustomRockGrading"
      :show-view-favorites-button="false"
      :show-move-to-archive-button="false"
      :show-duplicate-button="false"
      :show-delete-button="true"
      @add-item="addCustomRockGrading"
      @update-selected-items="customRockGradingStore.updateSelectedItems"
      @move-selected-items-to-archive="customRockGradingStore.moveSelectedItemsToArchive"
      @duplicate-selected-items="customRockGradingStore.duplicateSelectedItems"
      @delete-selected-items="customRockGradingStore.deleteSelectedItems"
    >
      <template #item="{ item }">
        <CustomRockGradingTableRow
          :item="item as CustomRockGrading"
          :is-checkbox-shown="isCheckboxShown"
        />
      </template>
    </DataTableWrapper>
    <DeleteDialog
      name="customrockgrading"
      v-model="deleteDialog"
      :bulk-delete="true"
      :items="selectedItems"
      :loading="deleteLoading"
      @bulk-delete="customRockGradingStore.bulkDelete($event)"
      @submitted-and-closed="customRockGradingStore.clearSelectedItems"
    />
  </template>
</template>

<style scoped></style>
